import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { saveAssignment, checkWordBalance, deductWordBalance } from '../../services/databaseService';
import { authenticatedPost, authenticatedPostFormData, handleApiResponse } from '../../utils/apiClient';
import AssignmentResult from './AssignmentResult';
import AuthModal from './AuthModal';
import GeneratingModal from './GeneratingModal';
import './QuestionBox.css';

// Determine API URL based on environment
const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    const directFunctionUrl = 'https://us-central1-clarifyfact-afa06.cloudfunctions.net/api';
    console.log('[API] Using direct function URL (production) to avoid 60-second hosting timeout');
    console.log('[API] Direct function URL:', directFunctionUrl);
    return directFunctionUrl;
  }
  
  if (process.env.NODE_ENV === 'production') {
    const directFunctionUrl = 'https://us-central1-clarifyfact-afa06.cloudfunctions.net/api';
    console.log('[API] Using direct function URL (NODE_ENV=production)');
    return directFunctionUrl;
  }
  
  console.log('[API] Using local backend (development)');
  return 'http://localhost:5000';
};

const API_BASE_URL = getApiBaseUrl();

const getApiUrl = (endpoint) => {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  if (API_BASE_URL.includes('/api')) {
    return `${API_BASE_URL}${cleanEndpoint}`;
  }
  
  return `${API_BASE_URL}/api${cleanEndpoint}`;
};

const getGenerateAssignmentUrl = () => {
  return API_BASE_URL;
};

const getUploadFilesUrl = () => {
  // Use separate Firebase Function for uploads (bypasses Express middleware)
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return 'https://us-central1-clarifyfact-afa06.cloudfunctions.net/uploadFiles';
  }
  return 'http://localhost:5000/api/upload-files';
};

function QuestionBox({ maxWords = 500 }) {
  const { currentUser, refreshUserProfile } = useAuth();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [hasShownAuthModal, setHasShownAuthModal] = useState(false);
  
  const [currentStep, setCurrentStep] = useState('question1');
  const [assignmentTopic, setAssignmentTopic] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [courseMaterials, setCourseMaterials] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [selectedOpinion, setSelectedOpinion] = useState(null);
  const [processingOpinionId, setProcessingOpinionId] = useState(null);
  const [showGeneratingModal, setShowGeneratingModal] = useState(false);
  const [factCheckResult, setFactCheckResult] = useState(null);
  const [finalAssignment, setFinalAssignment] = useState('');
  const [assignmentId, setAssignmentId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  
  const MAX_WORDS = maxWords;

  const getWords = (text) => text.trim().length === 0 ? [] : text.trim().split(/\s+/).filter(Boolean);
  const countWords = (text) => getWords(text).length;
  
  const limitToMaxWords = (text) => {
    const words = getWords(text);
    if (words.length <= MAX_WORDS) return text;
    return words.slice(0, MAX_WORDS).join(' ');
  };

  React.useEffect(() => {
    if (currentUser) {
      setHasShownAuthModal(false);
      setAuthModalOpen(false);
    }
  }, [currentUser]);

  const requireAuth = () => {
    if (!currentUser) {
      setAuthModalOpen(true);
      return false;
    }
    return true;
  };

  const handleQuestion1Submit = async (e) => {
    e.preventDefault();
    
    if (!requireAuth()) {
      setError('Sign In to generate tasks for free');
      return;
    }
    
    if (!assignmentTopic.trim()) {
      setError('Please enter your task topic.');
      return;
    }
    
    if (getWords(assignmentTopic).length > MAX_WORDS) {
      setError(`Answer exceeds ${MAX_WORDS}-word limit.`);
      return;
    }

    setError(null);
    setCurrentStep('question2');
  };

  const handleFileChange = async (e) => {
    if (!requireAuth()) {
      setError('Sign In to generate tasks for free');
      return;
    }
    
    const files = Array.from(e.target.files);
    const pdfFiles = files.filter(file => file.type === 'application/pdf');
    
    if (pdfFiles.length !== files.length) {
      setError('Please upload only PDF files.');
      return;
    }
    
    setError(null);
    await uploadFiles(pdfFiles);
  };

  // FIXED: Better handling of course materials extraction
  const uploadFiles = async (files) => {
    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      files.forEach((file, index) => {
        formData.append(`file${index}`, file);
      });
      
      const uploadUrl = getUploadFilesUrl();
      console.log('[upload-files] Uploading to:', uploadUrl);
      console.log('[upload-files] Files to upload:', files.map(f => f.name));
      
      const response = await authenticatedPostFormData(
        uploadUrl,
        formData
      );

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { message: errorText || 'Upload failed' };
        }
        
        if (response.status === 401) {
          const errorMessage = errorData.message || errorData.error || 'Authentication required. Please sign in again.';
          console.error('[upload-files] Authentication error:', errorMessage);
          throw new Error('Please sign in again');
        }
        
        throw new Error(errorData.message || errorData.error || 'Upload failed');
      }

      const result = await handleApiResponse(response);
      console.log('[upload-files] Upload result:', result);
      
      // CRITICAL FIX: Store the extracted text properly
      if (result.extractedText && result.extractedText.trim()) {
        console.log('[upload-files] ✅ Extracted text length:', result.extractedText.length);
        setCourseMaterials(result.extractedText);
      } else {
        console.warn('[upload-files] ⚠️ No extracted text from PDFs');
        setCourseMaterials(''); // Set empty instead of placeholder
      }
      
      const uploadResults = files.map((file, index) => ({
        name: file.name,
        size: file.size,
        type: file.type,
        storageId: result.fileIds?.[index] || result.files?.[index]?.path || `temp_${Date.now()}_${index}`,
        url: result.files?.[index]?.url || null
      }));

      setUploadedFiles([...uploadedFiles, ...uploadResults]);
      setError(null);
    } catch (err) {
      console.error('[upload-files] Upload error:', err);
      setError(`Upload failed: ${err.message}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // FIXED: Better validation before sending to backend
  const handleQuestion2Submit = async (e) => {
    e.preventDefault();
    
    if (!requireAuth()) {
      setError('Sign In to generate tasks for free');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const apiUrl = getApiUrl('/analyze-assignment');
      console.log('[analyze] Calling API:', apiUrl);
      console.log('[analyze] Assignment topic:', assignmentTopic);
      console.log('[analyze] Course materials length:', courseMaterials?.length || 0);
      console.log('[analyze] Has course materials:', !!courseMaterials);
      
      // CRITICAL FIX: Validate that we have either topic or materials
      if (!assignmentTopic.trim() && !courseMaterials.trim()) {
        setError('Please enter a topic or upload course materials');
        setIsLoading(false);
        return;
      }
      
      const response = await authenticatedPost(
        apiUrl,
        {
          assignmentTopic: assignmentTopic.trim(),
          courseMaterials: courseMaterials.trim(), // Send actual extracted text
        }
      );

      console.log('[analyze] Response status:', response.status);

      if (response.ok) {
        const result = await handleApiResponse(response);
        console.log('[analyze] ✅ Analysis result received');
        console.log('[analyze] Opinion options:', result.opinionOptions?.length || 0);
        setAnalysisResult(result);

        // Move to question 3 to select viewpoint
        setCurrentStep('question3');
        return;
      } else {
        const errorText = await response.text();
        console.error('[analyze] ❌ API error:', response.status, errorText);
        let errorMessage = `API returned ${response.status}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('[analyze] Error:', err);
      setError(`Failed to analyze task: ${err.message}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const generateAssignmentDirectly = async (analysisResult) => {
    console.log('Generating assignment with selected viewpoint:', selectedOpinion?.title || 'none selected');
    console.log('API_BASE_URL:', API_BASE_URL);
    
    if (isLoading) {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setShowGeneratingModal(true);

    try {
      let factCheckData = {
        isVerified: true,
        keyFacts: [],
        verifiedSources: []
      };
      setFactCheckResult(factCheckData);

      let requestedWordLimit = null;
      const wordLimitMatchTopic = assignmentTopic.match(/(?:within|about|approximately|around|less than|maximum|max|limit|up to|no more than)\s+(\d+)\s+words?/i);
      if (wordLimitMatchTopic) {
        requestedWordLimit = parseInt(wordLimitMatchTopic[1], 10);
        console.log(`[Word Limit] Detected word limit in assignment topic: ${requestedWordLimit} words`);
      }
      
      if (!requestedWordLimit && courseMaterials) {
        const wordLimitPatterns = [
          /(?:no more than|not more than|maximum|max|limit|up to|at most|less than|maximum of)\s+(\d+)\s+words?/gi,
          /(\d+)\s+words?\s+(?:maximum|max|limit|only|at most)/gi,
          /(?:within|about|approximately|around)\s+(\d+)\s+words?/gi,
          /word\s+(?:count|limit|maximum|max)\s*[:\-]?\s*(\d+)/gi,
          /(\d+)\s*[-—]\s*(\d+)\s+words?/gi,
        ];
        
        for (const pattern of wordLimitPatterns) {
          const matches = [...courseMaterials.matchAll(pattern)];
          for (const match of matches) {
            if (match[2]) {
              requestedWordLimit = Math.max(parseInt(match[1], 10), parseInt(match[2], 10));
              console.log(`[Word Limit] Detected word range in course materials: ${match[1]}-${match[2]} words, using upper limit: ${requestedWordLimit} words`);
              break;
            } else if (match[1]) {
              const detectedLimit = parseInt(match[1], 10);
              if (!requestedWordLimit || detectedLimit > requestedWordLimit) {
                requestedWordLimit = detectedLimit;
                console.log(`[Word Limit] Detected word limit in course materials: ${requestedWordLimit} words`);
              }
            }
          }
          if (requestedWordLimit) break;
        }
      }
      
      let estimatedWordCount;
      let targetWordRange = null;
      
      if (requestedWordLimit) {
        const isFromMaterials = !wordLimitMatchTopic;
        if (isFromMaterials && requestedWordLimit >= 2000) {
          targetWordRange = {
            min: Math.floor(requestedWordLimit * 0.88),
            max: requestedWordLimit
          };
          estimatedWordCount = Math.ceil(requestedWordLimit * 1.05);
          console.log(`[Word Limit] Course materials limit: ${requestedWordLimit} words, target range: ${targetWordRange.min}-${targetWordRange.max} words`);
        } else {
          estimatedWordCount = Math.ceil(requestedWordLimit * 1.2);
          console.log(`[Word Limit] Using ${requestedWordLimit} words with 20% buffer: ${estimatedWordCount} words`);
        }
      } else {
        estimatedWordCount = 1200;
        console.log(`[Balance Check] No word limit detected, using default estimate: ${estimatedWordCount} words`);
      }
      
      console.log(`[Balance Check] Estimated word count needed: ${estimatedWordCount} words${requestedWordLimit ? ` (requested: ${requestedWordLimit}${targetWordRange ? `, target range: ${targetWordRange.min}-${targetWordRange.max}` : ''})` : ' (default estimate)'}`);
      
      if (!currentUser) {
        setError('Sign In to generate tasks for free');
        setAuthModalOpen(true);
        setIsLoading(false);
        setShowGeneratingModal(false);
        return;
      }

      try {
        const balanceCheck = await checkWordBalance(currentUser.uid, estimatedWordCount);
        
        if (!balanceCheck.hasSufficientBalance) {
          const actualNeeded = requestedWordLimit || estimatedWordCount;
          setError(`INSUFFICIENT_BALANCE: You have ${balanceCheck.currentBalance.toLocaleString()} words, but need ${actualNeeded.toLocaleString()} words${requestedWordLimit ? ` (for ${requestedWordLimit}-word task)` : ''}. Please purchase more words to continue.`);
          setIsLoading(false);
          setShowGeneratingModal(false);
          return;
        }
      } catch (balanceError) {
        console.error('Error checking balance:', balanceError);
        setError('Error checking word balance. Please try again.');
        setIsLoading(false);
        setShowGeneratingModal(false);
        return;
      }

      try {
        console.log('Generating assignment (this should take under 1 minute)...');
        const generateUrl = getApiUrl('/generate-assignment');
        console.log('Generate assignment URL:', generateUrl);
        
        // CRITICAL FIX: Properly format the request data
        const generateRequestData = {
          assignmentTopic: assignmentTopic.trim(),
          // Only send course materials if we actually have them
          courseMaterials: courseMaterials && courseMaterials.trim()
            ? courseMaterials.trim()
            : '', // Send empty string if no materials
          verifiedSources: factCheckData.verifiedSources || [],
        };

        // Include selected opinion if available
        if (selectedOpinion) {
          generateRequestData.selectedOpinion = `${selectedOpinion.title}: ${selectedOpinion.description}`;
          console.log('[generate] Selected viewpoint:', selectedOpinion.title);
        }

        // Log what we're sending for debugging
        console.log('[generate] Request data:');
        console.log('- Topic:', generateRequestData.assignmentTopic.substring(0, 100) + '...');
        console.log('- Materials length:', generateRequestData.courseMaterials.length);
        console.log('- Has materials:', !!generateRequestData.courseMaterials);
        console.log('- Has selected viewpoint:', !!selectedOpinion);
        console.log('- Sources count:', generateRequestData.verifiedSources.length);

        if (requestedWordLimit) {
          generateRequestData.wordLimit = requestedWordLimit;
          if (targetWordRange) {
            generateRequestData.targetWordRange = targetWordRange;
          }
          console.log(`[Generate Request] Including word limit: ${requestedWordLimit}${targetWordRange ? `, target range: ${targetWordRange.min}-${targetWordRange.max}` : ''}`);
        }
        
        if (analysisResult && analysisResult.assignmentBlueprint) {
          generateRequestData.assignmentBlueprint = analysisResult.assignmentBlueprint;
          console.log(`[Generate Request] Including Assignment Blueprint with ${analysisResult.assignmentBlueprint.tasks?.length || 0} tasks`);
        }
        
        const generateResponse = await authenticatedPost(
          generateUrl,
          generateRequestData,
          { timeout: 120000 }
        );

        console.log('Generate response status:', generateResponse.status, generateResponse.statusText);
        
        if (generateResponse.ok) {
          const generateData = await handleApiResponse(generateResponse);
          console.log('Assignment generated successfully:', generateData);
          
          let assignmentText = generateData.assignment || '';
          
          if (requestedWordLimit) {
            const currentWordCount = countWords(assignmentText);
            console.log(`[Word Limit] Requested: ${requestedWordLimit} words, Current: ${currentWordCount} words`);
            
            if (targetWordRange) {
              if (currentWordCount > targetWordRange.max) {
                const words = getWords(assignmentText);
                const truncatedWords = words.slice(0, targetWordRange.max);
                assignmentText = truncatedWords.join(' ');
                console.log(`[Word Limit] Truncated assignment to ${targetWordRange.max} words (upper limit of target range)`);
              } else if (currentWordCount < targetWordRange.min) {
                console.log(`[Word Limit] Assignment is ${currentWordCount} words, below target range minimum (${targetWordRange.min} words). Consider regenerating for better coverage.`);
              } else {
                console.log(`[Word Limit] Assignment is ${currentWordCount} words, within target range (${targetWordRange.min}-${targetWordRange.max} words)`);
              }
            } else if (currentWordCount > requestedWordLimit) {
              const words = getWords(assignmentText);
              const truncatedWords = words.slice(0, requestedWordLimit);
              assignmentText = truncatedWords.join(' ');
              console.log(`[Word Limit] Truncated assignment to ${requestedWordLimit} words`);
            }
          }
          
          if (assignmentText && !generateData.humanized) {
            try {
              console.log('Humanizing assignment (this may take 2-5 minutes)...');
              const humanizeUrl = getApiUrl('/humanize-assignment');
              console.log('Humanize assignment URL:', humanizeUrl);
              console.log('Using 8-minute timeout for humanization (480 seconds)');
              
              const humanizeResponse = await authenticatedPost(
                humanizeUrl,
                {
                  assignment: assignmentText,
                },
                { timeout: 480000 }
              );
              
              if (humanizeResponse.ok) {
                const humanizeData = await handleApiResponse(humanizeResponse);
                console.log('Assignment humanized successfully:', humanizeData);
                assignmentText = humanizeData.assignment || assignmentText;
                
                if (requestedWordLimit) {
                  const currentWordCount = countWords(assignmentText);
                  if (targetWordRange) {
                    if (currentWordCount > targetWordRange.max) {
                      const words = getWords(assignmentText);
                      const truncatedWords = words.slice(0, targetWordRange.max);
                      assignmentText = truncatedWords.join(' ');
                      console.log(`[Word Limit] Truncated humanized assignment to ${targetWordRange.max} words (upper limit of target range)`);
                    } else {
                      console.log(`[Word Limit] Humanized assignment is ${currentWordCount} words, within target range (${targetWordRange.min}-${targetWordRange.max} words)`);
                    }
                  } else if (currentWordCount > requestedWordLimit) {
                    const words = getWords(assignmentText);
                    const truncatedWords = words.slice(0, requestedWordLimit);
                    assignmentText = truncatedWords.join(' ');
                    console.log(`[Word Limit] Truncated humanized assignment to ${requestedWordLimit} words`);
                  }
                }
              } else {
                console.warn('Humanization failed, using non-humanized assignment');
              }
            } catch (humanizeError) {
              console.error('Humanization error:', humanizeError);
              console.warn('Continuing with non-humanized assignment');
            }
          }
          
          const wordCount = countWords(assignmentText);
          console.log(`[Final] Assignment word count: ${wordCount} words${requestedWordLimit ? ` (limited to ${requestedWordLimit} words)` : ''}`);
          
          try {
            await deductWordBalance(currentUser.uid, wordCount);
            console.log(`Deducted ${wordCount} words from balance (actual usage, not estimate)`);
            if (refreshUserProfile) {
              await refreshUserProfile();
            }
          } catch (deductError) {
            console.error('Error deducting word balance:', deductError);
          }
          
          setFinalAssignment(assignmentText);
          setCurrentStep('result');
          
          if (currentUser) {
            try {
              setSaveStatus('saving');
              const assignmentId = await saveAssignment(currentUser.uid, {
                assignmentTopic,
                courseMaterials,
                selectedOpinion: null,
                summary: analysisResult?.summary || '',
                finalAssignment: assignmentText,
                verifiedSources: factCheckData?.verifiedSources || [],
                uploadedFiles: uploadedFiles.map(f => ({
                  name: f.name,
                  size: f.size,
                  type: f.type,
                  storageId: f.storageId
                }))
              });
              setAssignmentId(assignmentId);
              setSaveStatus('saved');
              console.log('Assignment saved to Firestore:', assignmentId);
            } catch (saveError) {
              console.error('Error saving assignment:', saveError);
              setSaveStatus('error');
            }
          }
        } else {
          const errorText = await generateResponse.text();
          console.error('Generate failed. Response:', errorText);
          throw new Error(`Generate failed: ${generateResponse.status} ${generateResponse.statusText}. ${errorText}`);
        }
      } catch (generateError) {
        console.error('Generate assignment error:', generateError);
        console.error('Error details:', {
          name: generateError.name,
          message: generateError.message,
          stack: generateError.stack
        });
        
        let errorMessage = 'Unknown error';
        if (generateError.name === 'AbortError') {
          errorMessage = 'Request timed out. Please try again.';
        } else if (generateError.message) {
          errorMessage = generateError.message;
        } else if (generateError.toString) {
          errorMessage = generateError.toString();
        }
        
        setError(`Failed to generate task: ${errorMessage}. If this persists, check the browser console (F12) for details.`);
        setIsLoading(false);
        setShowGeneratingModal(false);
        return;
      }
    } catch (err) {
      console.error('Error generating assignment:', err);
      setError('Failed to generate task. Please try again.');
      setIsLoading(false);
      setShowGeneratingModal(false);
    } finally {
      setIsLoading(false);
      setShowGeneratingModal(false);
    }
  };

  if (showGeneratingModal) {
    return (
      <>
        <GeneratingModal />
        {error && !isLoading && (
          <div style={{ 
            margin: '20px auto', 
            maxWidth: '600px', 
            padding: '15px', 
            backgroundColor: error.includes('INSUFFICIENT_BALANCE') ? '#f8d7da' : '#fff3cd', 
            border: error.includes('INSUFFICIENT_BALANCE') ? '1px solid #f5c6cb' : '1px solid #ffc107',
            borderRadius: '5px',
            color: error.includes('INSUFFICIENT_BALANCE') ? '#721c24' : '#856404'
          }}>
            <strong>⚠️ {error.includes('INSUFFICIENT_BALANCE') ? 'Insufficient Balance:' : 'Error:'}</strong> 
            <div style={{ marginTop: '10px' }}>
              {error.replace('INSUFFICIENT_BALANCE: ', '')}
            </div>
            {error.includes('INSUFFICIENT_BALANCE') && (
              <div style={{ marginTop: '10px' }}>
                <Link to="/pricing" style={{ 
                  color: '#28a745', 
                  textDecoration: 'underline',
                  fontWeight: '600'
                }}>
                  Purchase more words →
                </Link>
              </div>
            )}
            {!error.includes('INSUFFICIENT_BALANCE') && (
              <button 
                onClick={() => {
                  setError(null);
                  setIsLoading(false);
                }}
                style={{
                  marginTop: '10px',
                  padding: '8px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Try Again
              </button>
            )}
          </div>
        )}
      </>
    );
  }

  // Question 3: Select viewpoint
  if (currentStep === 'question3') {
    return (
      <>
        <div className="text-input-section">
          <div className="input-container">
            <div className="input-header">
              <label className="input-label">Question 3</label>
            </div>

            <div className="question-display">
              <p className="question-text">
                Choose a viewpoint/perspective to approach your assignment:
              </p>
            </div>

            {/* Summary */}
            {analysisResult?.summary && (
              <div style={{
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '20px',
                border: '1px solid #dee2e6'
              }}>
                <h4 style={{ marginTop: 0, marginBottom: '10px', color: '#495057' }}>
                  Assignment Overview:
                </h4>
                <p style={{ margin: 0, lineHeight: '1.6', color: '#6c757d' }}>
                  {analysisResult.summary}
                </p>
              </div>
            )}

            {/* Opinion Options */}
            {analysisResult?.opinionOptions && analysisResult.opinionOptions.length > 0 ? (
              <div style={{ marginTop: '20px' }}>
                {analysisResult.opinionOptions.map((option, index) => (
                  <div
                    key={option.id || index}
                    onClick={() => {
                      if (!isLoading) {
                        setSelectedOpinion(option);
                      }
                    }}
                    style={{
                      padding: '20px',
                      marginBottom: '15px',
                      border: selectedOpinion?.id === option.id ? '2px solid #28a745' : '2px solid #dee2e6',
                      borderRadius: '8px',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      backgroundColor: selectedOpinion?.id === option.id ? '#e7f5ec' : 'white',
                      transition: 'all 0.3s',
                      opacity: isLoading ? 0.6 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!isLoading && selectedOpinion?.id !== option.id) {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isLoading && selectedOpinion?.id !== option.id) {
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        border: selectedOpinion?.id === option.id ? '2px solid #28a745' : '2px solid #dee2e6',
                        backgroundColor: selectedOpinion?.id === option.id ? '#28a745' : 'white',
                        marginRight: '15px',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {selectedOpinion?.id === option.id && (
                          <span style={{ color: 'white', fontSize: '14px', fontWeight: 'bold' }}>✓</span>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          margin: '0 0 10px 0',
                          color: '#212529',
                          fontSize: '16px',
                          fontWeight: '600'
                        }}>
                          {option.title}
                        </h4>
                        <p style={{
                          margin: 0,
                          color: '#6c757d',
                          lineHeight: '1.6',
                          fontSize: '14px'
                        }}>
                          {option.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#6c757d', textAlign: 'center', padding: '20px' }}>
                No viewpoint options available. Generating assignment with default approach...
              </p>
            )}

            {/* Error Display */}
            {error && (
              <div style={{
                padding: '15px',
                marginTop: '15px',
                backgroundColor: '#f8d7da',
                color: '#721c24',
                borderRadius: '5px',
                border: '1px solid #f5c6cb'
              }}>
                ⚠️ {error}
              </div>
            )}

            {/* Generate Button */}
            <button
              onClick={async () => {
                if (selectedOpinion) {
                  await generateAssignmentDirectly(analysisResult);
                } else {
                  setError('Please select a viewpoint to continue');
                }
              }}
              disabled={isLoading || !selectedOpinion}
              style={{
                marginTop: '20px',
                padding: '12px 24px',
                backgroundColor: (!selectedOpinion || isLoading) ? '#6c757d' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: (!selectedOpinion || isLoading) ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                width: '100%',
                transition: 'background-color 0.3s'
              }}
            >
              {isLoading ? 'Generating Assignment...' : 'Generate Assignment with Selected Viewpoint'}
            </button>
          </div>
        </div>
      </>
    );
  }

  if (currentStep === 'result') {
    return (
      <AssignmentResult
        assignment={finalAssignment}
        isLoading={false}
        error={error}
        saveStatus={saveStatus}
        assignmentId={assignmentId}
        isAuthenticated={!!currentUser}
      />
    );
  }

  const isQuestion2 = currentStep === 'question2';

  return (
    <>
      <AuthModal 
        isOpen={authModalOpen} 
        onClose={() => {
          setAuthModalOpen(false);
          if (!currentUser) {
            setHasShownAuthModal(false);
          }
        }}
        initialMode="login"
        message={hasShownAuthModal && !currentUser ? "Sign In to Continue" : null}
      />
      <div className="text-input-section">
        <div className="input-container">
          <div className="input-header">
            <label htmlFor={isQuestion2 ? "file-input" : "question-input"} className="input-label">
              {isQuestion2 ? 'Question 2' : 'Question 1'}
            </label>
          </div>
          
          <div className="question-display">
            <p className="question-text">
              {isQuestion2 
                ? "Upload Your Educational Slides / Class Files (PDF) (Optional)"
                : "What is your task topic?"
              }
            </p>
          </div>
        
        {isQuestion2 ? (
          <div className="file-upload-section">
            <div className="file-upload-area">
              <input
                type="file"
                id="file-input"
                accept=".pdf"
                multiple
                onChange={handleFileChange}
                disabled={isLoading}
                className="file-input"
              />
              <label htmlFor="file-input" className="file-upload-label">
                <div className="file-upload-content">
                  <span className="file-upload-icon">📄</span>
                  <p className="file-upload-text">Click to upload PDF files</p>
                  <p className="file-upload-hint">or drag and drop</p>
                </div>
              </label>
            </div>
            
            {uploadedFiles.length > 0 && (
              <div className="uploaded-files-list">
                <h4>Uploaded Files:</h4>
                <ul>
                  {uploadedFiles.map((file, index) => (
                    <li key={index} className="uploaded-file-item">
                      <span className="file-name">✓ {file.name}</span>
                      <button 
                        type="button"
                        onClick={() => removeFile(index)}
                        className="remove-file-btn"
                        disabled={isLoading}
                      >
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <textarea 
            id="question-input"
            className="text-area" 
            placeholder="Enter your task topic (e.g., 'The impact of climate change on global economies')..."
            value={assignmentTopic}
            onChange={(e) => {
              const newValue = e.target.value;
              const newWordCount = getWords(newValue).length;
              
              if (!currentUser && !hasShownAuthModal && newWordCount >= 2 && newWordCount <= 3) {
                setAuthModalOpen(true);
                setHasShownAuthModal(true);
              }
              
              const currentWordCount = getWords(assignmentTopic).length;
              
              if (newWordCount <= MAX_WORDS || newValue.length < assignmentTopic.length || newWordCount < currentWordCount) {
                setAssignmentTopic(newValue);
                if (newWordCount >= MAX_WORDS) {
                  setError(`Limit reached: ${MAX_WORDS} words.`);
                } else if (error && error.toLowerCase().includes('limit')) {
                  setError(null);
                }
              }
            }}
            disabled={isLoading}
          />
        )}

        {/* Submit Button */}
        {!isQuestion2 && (
          <button
            onClick={async (e) => {
              e.preventDefault();

              if (!requireAuth()) {
                return;
              }

              if (!assignmentTopic.trim()) {
                setError('Please enter a task topic before continuing.');
                return;
              }

              // Move to question 2 (file upload)
              setCurrentStep('question2');
              setError(null);
            }}
            disabled={isLoading || !assignmentTopic.trim()}
            style={{
              marginTop: '15px',
              padding: '12px 24px',
              backgroundColor: (!assignmentTopic.trim() || isLoading) ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: (!assignmentTopic.trim() || isLoading) ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              width: '100%',
              transition: 'background-color 0.3s'
            }}
          >
            {isLoading ? 'Processing...' : 'Continue to Upload Files'}
          </button>
        )}

        {isQuestion2 && (
          <button
            onClick={handleQuestion2Submit}
            disabled={isLoading}
            style={{
              marginTop: '15px',
              padding: '12px 24px',
              backgroundColor: isLoading ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              width: '100%',
              transition: 'background-color 0.3s'
            }}
          >
            {isLoading ? 'Analyzing...' : 'Analyze & Generate Assignment'}
          </button>
        )}

        {error && (
          <div className="error-message" style={{
            marginTop: '10px',
            padding: '10px',
            backgroundColor: error.includes('INSUFFICIENT_BALANCE') ? '#f8d7da' : error.includes('Sign In') ? '#d1ecf1' : '#fff3cd',
            border: error.includes('INSUFFICIENT_BALANCE') ? '1px solid #f5c6cb' : error.includes('Sign In') ? '1px solid #bee5eb' : '1px solid #ffc107',
            borderRadius: '5px',
            color: error.includes('INSUFFICIENT_BALANCE') ? '#721c24' : error.includes('Sign In') ? '#0c5460' : '#856404'
          }}>
            {error.includes('INSUFFICIENT_BALANCE') ? (
              <>
                <strong>⚠️ Insufficient Balance:</strong> {error.replace('INSUFFICIENT_BALANCE: ', '')}
                <div style={{ marginTop: '10px' }}>
                  <Link to="/pricing" style={{
                    color: '#28a745',
                    textDecoration: 'underline',
                    fontWeight: '600'
                  }}>
                    Purchase more words →
                  </Link>
                </div>
              </>
            ) : (
              <>
                <strong>⚠️ Error:</strong> {error}
                {!error.includes('Sign In') && (
                  <button
                    onClick={() => {
                      setError(null);
                      setIsLoading(false);
                    }}
                    style={{
                      marginTop: '10px',
                      padding: '8px 16px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Try Again
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
}

export default QuestionBox;